// src/features/etf_static_data/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  AutocompleteAsyncField,
  CheckboxField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  distributionPolicyOptions,
  replicationMethodologyOptions,
  sfdrOptions,
} from "./filter";
import { createFormSchema } from "./form-utils";
import { openFundFieldsTranslations } from "./open_fund_fields_config";
import { OpenFundFieldsSection } from "./OpenFundFieldsSection";

const FormSchema = createFormSchema();

export function EtfStaticDataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "strategy_name" && fetchedData.strategy_id) {
        // Use the actual strategy name from the backend response
        const strategyName =
          fetchedData.strategy_name || `Strategy ${fetchedData.strategy_id}`;
        return {
          text: strategyName,
          id: fetchedData.strategy_id,
          name: strategyName,
        };
      }

      // Convert number fields to strings for SelectField compatibility
      if (key === "replication_methodology" && typeof value === "number") {
        return value.toString();
      }
      if (key === "distribution_policy" && typeof value === "number") {
        return value.toString();
      }

      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                EtfStaticData {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.strategy_name`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="etl-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    name="benchmark_name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Benchmark Name",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="replication_methodology"
                    label={t(`${table_name}.columns.replication_methodology`, {
                      ns: "tables",
                      defaultValue: "Replication Methodology",
                    })}
                    options={replicationMethodologyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="sfdr"
                    label={t(`${table_name}.columns.sfdr`, {
                      ns: "tables",
                      defaultValue: "SFDR",
                    })}
                    options={sfdrOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="distribution_policy"
                    label={t(`${table_name}.columns.distribution_policy`, {
                      ns: "tables",
                      defaultValue: "Distribution Policy",
                    })}
                    options={distributionPolicyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <CheckboxField
                    name="is_funded"
                    label={t(`${table_name}.columns.is_funded`, {
                      ns: "tables",
                      defaultValue: "Is Funded",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_leveraged"
                    label={t(`${table_name}.columns.is_leveraged`, {
                      ns: "tables",
                      defaultValue: "Is Leveraged",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_ucits"
                    label={t(`${table_name}.columns.is_ucits`, {
                      ns: "tables",
                      defaultValue: "Is UCITS",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Open Fund Fields */}
                <OpenFundFieldsSection control={form.control} />
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirm creation */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["strategy_id"], openFundFieldsTranslations)
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel creation */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
